import { Header } from "semantic-ui-react";
import { useHistory, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import {
  InputCheckBox,
  InputSelect,
  InputText,
  InputTextBox,
  InputToggle,
} from "../../../components/Inputs/Inputs";
import BackButton from "../../../components/BackButton/BackButton";
import { api } from "../../../api/api";
import { useToast } from "../../../components/Toast";
import {
  all_first_category_url,
  product_property_type_url,
  product_property_value_url,
  all_second_category_url,
  all_third_category_url,
} from "../../../api/urls";
import unknownError from "../../../utils/unknownError";
import Loading from "../../../components/Loading/Loading";
import { moveUpToError } from "../../../utils/moveUpToError";
import { APIDropdown } from "../../../components/APIDropdown/APIDropdown";
import { modules } from "../../../api/codes";
import { ApprovalText } from "../../../utils/approveLevel";

const initialState = {
  description: "",
  printable_name: "",
  disable: false,
  is_active: false,
  code: "",
  name: "",
  ui_code: "",
  default_filter: false,
  category_type: "",
  cat_first: null,
  cat_second: null,
  cathird: "",
  product_property_value: "",
};

const module = modules.PropertyType;

const title = "Property Type";
const backUrl = "/masters/property_type";
const apiUrl = product_property_type_url;

const NewPropertyType = ({ edit, view }) => {
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const history = useHistory();
  const params = useParams();
  const toast = useToast();
  const [isGetting, setIsGetting] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [aprvlLoader, setAprvlLoader] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (edit || view || params.id) {
      const getData = async () => {
        try {
          const res = await api.get(apiUrl + params.id + "/", {
            headers: {
              module: module,
            },
          });
          setData(res.data);
          setIsGetting(false);
        } catch (err) {
          unknownError(err);
          setIsGetting(false);
        }
      };

      getData();
    } else {
      setIsGetting(false);
    }
  }, [params.id, edit, view, refresh]);

  const set = (key, value) => {
    setData((d) => {
      const newData = { ...d };
      newData[key] = value;
      return newData;
    });
  };

  if (isGetting) return <Loading />;

  return (
    <form
      id="form-wrapper"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
          if (edit && params.id) {
            api
              .patch(apiUrl + data.id + "/", data, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Edited data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          } else {
            api
              .post(apiUrl, data, {
                headers: {
                  "Content-Type": "application/json",
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Added data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          }
        } catch (err) {
          unknownError(err);
          setIsSaving(false);
        }
      }}
    >
      <div id="form">
        <BackButton href={backUrl} />
        <Header>{title}</Header>
        <InputText
          label="Code"
          placeholder="Code"
          disabled={view}
          value={data.code}
          error={errors["code"]}
          required
          onChange={(v) => set("code", v)}
        />
        <InputText
          label="Printable name"
          placeholder="Printable name"
          disabled={view}
          value={data.printable_name}
          error={errors["printable_name"]}
          required
          onChange={(v) => set("printable_name", v)}
        />
        <InputTextBox
          label="Description"
          placeholder="Description"
          disabled={view}
          value={data.description}
          error={errors["description"]}
          required
          onChange={(v) => set("description", v)}
        />
        <InputText
          label="Name"
          placeholder="Name"
          disabled={view}
          value={data.name}
          error={errors["name"]}
          required
          onChange={(v) => set("name", v)}
        />
        <InputText
          label="Ui code"
          placeholder="Ui code"
          disabled={view}
          value={data.ui_code}
          error={errors["ui_code"]}
          onChange={(v) => set("ui_code", v)}
        />
        <InputSelect
          label="Category type"
          placeholder="Category type"
          disabled={view}
          value={data.category_type}
          error={errors["category_type"]}
          options={["1", "2", "3"]}
          required
          onChange={(v) => set("category_type", v)}
        />
        <APIDropdown
          url={all_first_category_url}
          label="First category"
          placeholder="First category"
          disabled={view}
          value={data.cat_first}
          error={errors["cat_first"]}
          required
          onChange={(v) => set("cat_first", v)}
          module={module}
          displayAs={(x) =>
            `${x.printable_name} ${x.is_global ? "(global)" : ""} ${
              x.is_b2b ? "(B2B)" : ""
            }`
          }
        />
        <APIDropdown
          url={
            all_second_category_url +
            (data.cat_first ? `?category_first=${data.cat_first}` : "")
          }
          label="Second category"
          placeholder="Second category"
          disabled={view}
          value={data.cat_second}
          error={errors["cat_second"]}
          required
          onChange={(v) => set("cat_second", v)}
          module={module}
          displayAs={(x) =>
            `${x.printable_name} ${x.is_global ? "(global)" : ""} ${
              x.is_b2b ? "(B2B)" : ""
            }`
          }
        />
        <APIDropdown
          url={
            all_third_category_url +
            (data.cat_second ? `?category_second=${data.cat_second}` : "")
          }
          label="Third category"
          placeholder="Third category"
          disabled={view}
          value={data.cathird}
          error={errors["cathird"]}
          required
          onChange={(v) => set("cathird", v)}
          module={module}
          displayAs={(x) =>
            `${x.printable_name} ${x.is_global ? "(global)" : ""} ${
              x.is_b2b ? "(B2B)" : ""
            }`
          }
        />
        <APIDropdown
          label="Product property value"
          placeholder="Product property value"
          disabled={view}
          value={data.product_property_value}
          error={errors["product_property_value"]}
          url={product_property_value_url}
          hasMultipleSelection
          parameterForGet="name"
          required
          onChange={(v) => set("product_property_value", v)}
          module={module}
        />
        <InputCheckBox
          label="Default filter"
          disabled={view}
          value={data.default_filter}
          error={errors["default_filter"]}
          onChange={(v) => set("default_filter", v)}
        />
        <InputToggle
          label="Active"
          disabled={view}
          value={data.is_active}
          error={errors["is_active"]}
          onChange={(v) => set("is_active", v)}
        />

        <ApprovalText
          id={params.id}
          level={data?.approval}
          setRefresh={setRefresh}
          setAprvlLoader={setAprvlLoader}
          toast={toast}
          view={view}
          aprvlLoader={aprvlLoader}
          module={module}
          apiUrl={apiUrl}
        />
        {!view && (
          <>
            <div style={{ padding: "10px" }} />
            <div className="actions">
              <button
                className="btn-red"
                type="button"
                onClick={() => {
                  history.push(backUrl);
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => {
                  moveUpToError();
                }}
                disabled={isSaving}
              >
                Save
              </button>
            </div>
          </>
        )}
        <div style={{ padding: "10px" }} />
      </div>
    </form>
  );
};

export default NewPropertyType;
